import { useEffect } from 'react';

import { FaqEntry } from '../../../../../features/faq/components/FaqEntry';
import type { PublicFAQ } from '../../../../../features/faq/publicApi';
import { useI18n } from '../../../../../shared/i18n';
import { useData } from '../../../../../shared/lib';
import { Container, EmptyState, Eyebrow, Section } from '../../../../../shared/ui';

/**
 * Native `<details>`/`<summary>` rather than the Radix `Accordion` used in
 * admin: a visitor's browser never sends the URL fragment to the server, so
 * a `#faq-<id>` deep link can only ever be honoured client-side, and a
 * component that unmounts its closed panels until JavaScript runs would
 * fail "works before hydration" (docs/features.md §8) on that first paint.
 * `<details>` needs neither — closed content is already in the DOM, and the
 * browser scrolls a fragment link to it, open or not, with zero JavaScript.
 */
export default function Page() {
  const { t } = useI18n();
  const faq = useData<PublicFAQ>();

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash.startsWith('faq-')) return;
    const target = document.getElementById(hash);
    if (target instanceof HTMLDetailsElement) {
      target.open = true;
      target.scrollIntoView({ block: 'center' });
    }
  }, []);

  const grouped = faq.categories
    .map((category) => ({
      category,
      entries: faq.data.filter((entry) => entry.category_id === category.id),
    }))
    .filter((group) => group.entries.length > 0);
  const uncategorised = faq.data.filter((entry) => !entry.category_id);

  return (
    <Section spacing="lg">
      <Container className="flex max-w-prose flex-col gap-8">
        <div className="flex flex-col gap-3">
          {/* A route, not a translated label — see the same choice on the
              other listings. */}
          <Eyebrow>
            <span dir="ltr">/faq</span>
          </Eyebrow>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            {t('common.faq.heading')}
          </h1>
        </div>

        {faq.data.length === 0 ? (
          <EmptyState
            title={t('common.faq.emptyTitle')}
            description={t('common.faq.emptyDescription')}
          />
        ) : grouped.length > 0 ? (
          <div className="flex flex-col gap-8">
            {grouped.map(({ category, entries }) => (
              <div key={category.id} className="flex flex-col">
                <h2 className="mb-2 text-sm font-semibold tracking-wide text-text-subtle uppercase">
                  {category.name}
                </h2>
                {entries.map((entry) => (
                  <FaqEntry
                    key={entry.id}
                    id={entry.id}
                    question={entry.question}
                    answer={entry.answer}
                  />
                ))}
              </div>
            ))}
            {uncategorised.map((entry) => (
              <FaqEntry
                key={entry.id}
                id={entry.id}
                question={entry.question}
                answer={entry.answer}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col">
            {faq.data.map((entry) => (
              <FaqEntry
                key={entry.id}
                id={entry.id}
                question={entry.question}
                answer={entry.answer}
              />
            ))}
          </div>
        )}
      </Container>
    </Section>
  );
}
